import TreeItem, { TreeItemProps } from '@mui/lab/TreeItem';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { SvgIconProps } from '@mui/material/SvgIcon';
import Typography from '@mui/material/Typography';
import React from 'react';

const PREFIX = 'StyledTreeItem';

const classes = {
  root: `${PREFIX}-root`,
  content: `${PREFIX}-content`,
  group: `${PREFIX}-group`,
  expanded: `${PREFIX}-expanded`,
  selected: `${PREFIX}-selected`,
  label: `${PREFIX}-label`,
  labelRoot: `${PREFIX}-labelRoot`,
  labelIcon: `${PREFIX}-labelIcon`,
  labelText: `${PREFIX}-labelText`
};

type StyledTreeItemProps = TreeItemProps & {
  labelIcon: React.ElementType<SvgIconProps>;
  labelInfo?: string;
  labelText: string;
  selected?: boolean;
  onLabelClick: (event: React.MouseEvent<HTMLDivElement>) => void;
};

const Root = styled('div')(({ theme }) => ({
  [`& .${classes.root}`]: {},
  [`& .${classes.content}`]: {
    color: theme.palette.text.secondary,
    borderTopRightRadius: theme.spacing(2),
    borderBottomRightRadius: theme.spacing(2),
    paddingRight: theme.spacing(1),
    paddingLeft: theme.spacing(0),
    fontWeight: theme.typography.fontWeightMedium,
    '.MuiTreeItemExpanded > &': {
      fontWeight: theme.typography.fontWeightRegular
    }
  },

  [`& .${classes.group}`]: {
    marginLeft: theme.spacing(1),
    '& .MuiTreeItem-content': {
      paddingLeft: theme.spacing(2)
    }
  },

  [`& .${classes.expanded}`]: {},
  [`& .${classes.selected}`]: {},

  [`& .${classes.label}`]: {
    fontWeight: 'inherit',
    color: 'inherit'
  },

  [`&.${classes.labelRoot}`]: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0.5, 0)
  },

  [`& .${classes.labelIcon}`]: {
    marginRight: theme.spacing(1),
    fontSize: 'inherit'
  },

  [`& .${classes.labelText}`]: {
    fontWeight: 'inherit',
    flexGrow: 1
  }
}));

const StyledTreeItem = ({
  labelText,
  labelIcon: LabelIcon,
  labelInfo,
  onLabelClick,
  selected,
  ...rest
}: StyledTreeItemProps) => {
  return (
    <Root
      sx={{
        color: 'inherit',
        '&:hover > .MuiTreeItem-content': {
          backgroundColor: 'action.hover'
        },
        '&:focus > .MuiTreeItem-content, & .StyledTreeItem-selected > .MuiTreeItem-content': {
          backgroundColor: theme => `var(--tree-view-bg-color, ${theme.palette.grey[400]})`,
          color: 'var(--tree-view-color)'
        },
        '&:focus > .MuiTreeItem-content .MuiTreeItem-label, &:hover > .MuiTreeItem-content .MuiTreeItem-label, &.MuiTreeItem-selected > .MuiTreeItem-content .MuiTreeItem-label':
          {
            backgroundColor: 'transparent'
          }
      }}
    >
      <TreeItem
        label={
          <Box
            sx={{ display: 'flex', alignItems: 'center', padding: theme => theme.spacing(0.5, 0) }}
            role="treeitem"
            tabIndex={-1}
            onKeyPress={() => console.log('key pressed')}
            onClick={onLabelClick}
            data-testid="hierarchy-tree-item-label"
          >
            <LabelIcon color="inherit" className={classes.labelIcon} />
            <Typography variant="body2" className={classes.labelText}>
              {labelText}
            </Typography>
            <Typography variant="caption" color="inherit">
              {labelInfo}
            </Typography>
          </Box>
        }
        className={selected ? classes.selected : ''}
        classes={{
          root: classes.root,
          content: classes.content,
          expanded: classes.expanded,
          selected: classes.selected,
          group: classes.group,
          label: classes.label
        }}
        {...rest}
      />
    </Root>
  );
};

export default StyledTreeItem;
